import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { Link, RouteComponentProps, useNavigate } from '@reach/router';
import { usePolicyStreamGoogleAnalytics } from '../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../hooks/usePolicyStreamGoogleAnalytics/types';
import { PolicyStreamButtonsContainer } from './components';
import { PolicyStreamPageContainer } from './components/PageContainer';
import { policyStreamPaths } from './policyStreamConstants';
import { Form, FormState } from 'informed';
import { WithFormStyles, withFormStyles } from '../../util/withFormStyles';
import { styled } from '@mui/material';
import { useSubmitPolicyStreamQuote } from '../../hooks';
import { PolicyStreamLayout } from './components';
import { useEnsureFormPresent } from '../../hooks/useEnsureFormPresent';
import { POLICYSTREAM_FORMS } from './constants';
import RenderedQuestionInstance from '../common/QuoteComponents/RenderedQuestionInstance';
import { POLICYSTREAM_API_NAMES } from './constants';
import { ErrorP } from '../common/Typography';
import { useDispatch, useSelector } from 'react-redux';
import {
  generateGetAnsweredForm,
  generateSubmitPolicyStreamFormAnswersThunk,
  getAnsweredForms,
} from '../../store/policystreamStore';
import {
  answerInstancesToFormState,
  formStateToAnswerInstances,
} from '../QuoteWizard/QuoteForms/utility';
import { useScrollToTopOnInitialMount } from '@calefy-inc/hooks';
import { PolicyStreamFormCard } from './components/PolicyStreamFormCard';
import { AgreeToTosComponent } from './components/AgreeToTosComponent';
import { errorify } from '@calefy-inc/utility';

interface PolicyStreamEmailSavePageProps
  extends RouteComponentProps,
    WithFormStyles {}
export const PolicyStreamEmailSavePage = withFormStyles()(
  ({ classes }: PolicyStreamEmailSavePageProps) => {
    const myAnalytics = usePolicyStreamGoogleAnalytics();

    useScrollToTopOnInitialMount();
    const [gentleSubmissionError, setGentlySubmissionError] =
      useState<string>('');

    // Send Google Analytics event on component load
    useEffect(() => {
      myAnalytics?.triggerEvent(
        GAEventNames.EmailSave,
        EventActions.PageLoad,
        'PolicyStream Email Save Page',
      );
    }, [myAnalytics]);
    const [requestNavigateForward, setRequestNavigateForward] =
      useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const basicClientInfoForm = useEnsureFormPresent({
      internalName: POLICYSTREAM_FORMS.basicClientInfo,
    });
    const answeredForms = useSelector(getAnsweredForms);
    const basicClientInfoAnswers = useSelector(
      generateGetAnsweredForm({
        internalName: POLICYSTREAM_FORMS.basicClientInfo,
      }),
    );
    const submitQuote = useSubmitPolicyStreamQuote({ status: 'IN_PROGRESS' });
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
      if (requestNavigateForward && basicClientInfoAnswers) {
        submitQuote({
          clientEmail: email,
          clientName: firstName,
          formsWithAnswers: [
            ...answeredForms.map((form) => form.toUnifiedCompletedForm()),
            basicClientInfoAnswers.form.toDefaultUnifiedCompletedForm(),
          ],
        });
        navigate(policyStreamPaths.recommendations.url);
      }
    }, [
      requestNavigateForward,
      basicClientInfoAnswers,
      answeredForms,
      email,
      firstName,
      submitQuote,
      navigate,
    ]);

    return (
      <PolicyStreamLayout>
        <PolicyStreamPageContainer>
          <Typography variant='h2'>Save Application</Typography>
          <p>
            Enter your email if you want us to save your information (optional)
          </p>
          <PolicyStreamFormCard>
            <Form
              className={classes.form}
              initialValues={
                basicClientInfoAnswers
                  ? answerInstancesToFormState(basicClientInfoAnswers.answers)
                  : undefined
              }
              onSubmit={(formState: FormState) => {
                const { values } = formState;
                if (!basicClientInfoForm) {
                  setGentlySubmissionError('Please wait for the form');
                  return;
                }
                const email = values[
                  POLICYSTREAM_API_NAMES.basicClientInfo.email
                ]
                  ? String(values[POLICYSTREAM_API_NAMES.basicClientInfo.email])
                  : '';
                const firstName = values[
                  POLICYSTREAM_API_NAMES.basicClientInfo.firstName
                ]
                  ? String(
                      values[POLICYSTREAM_API_NAMES.basicClientInfo.firstName],
                    )
                  : '';
                if (!email) {
                  setGentlySubmissionError(
                    'Email is required to save your application',
                  );
                  return;
                }
                setEmail(email);
                setFirstName(firstName);
                const answers = formStateToAnswerInstances(
                  values,
                  basicClientInfoForm?.questionInstances || [],
                );
                dispatch(
                  generateSubmitPolicyStreamFormAnswersThunk({
                    form: basicClientInfoForm,
                    answers,
                  }),
                ); // NB we wait for the answers to be in state before we actually navigate
                setRequestNavigateForward(true);
              }}
              onSubmitFailure={(error: unknown) => {
                setGentlySubmissionError(errorify(error).message);
              }}
            >
              {({ formState }: { formState: FormState }) => {
                return (
                  <>
                    {basicClientInfoForm
                      ? basicClientInfoForm.questionInstances
                          .filter((q) =>
                            [
                              POLICYSTREAM_API_NAMES.basicClientInfo.firstName,
                              POLICYSTREAM_API_NAMES.basicClientInfo.email,
                              // @ts-expect-error
                            ].includes(q.apiName),
                          )
                          .map((q) => (
                            <RenderedQuestionInstance
                              questionInstance={q}
                              classes={classes}
                            />
                          ))
                      : null}
                    {gentleSubmissionError ? (
                      <ErrorP>
                        Error submitting form: {gentleSubmissionError}. If you'd
                        like to continue without saving, press "No Thanks".
                      </ErrorP>
                    ) : null}
                    <Collapse in={Object.values(formState.values).length > 0}>
                      <AgreeToTosComponent />
                    </Collapse>
                    <ButtonsContainer>
                      <Button
                        component={Link}
                        to={policyStreamPaths.personalize.url}
                      >
                        Back
                      </Button>
                      <PolicyStreamButtonsContainer>
                        <Button
                          component={Link}
                          to={policyStreamPaths.recommendations.url}
                        >
                          No, Thanks
                        </Button>
                        <Button type='submit' variant='contained'>
                          Save
                        </Button>
                      </PolicyStreamButtonsContainer>
                    </ButtonsContainer>
                  </>
                );
              }}
            </Form>
          </PolicyStreamFormCard>
        </PolicyStreamPageContainer>
      </PolicyStreamLayout>
    );
  },
);

const ButtonsContainer = styled('div')(({ theme }) => {
  return {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  };
});
